import StripeInvoice from "@/types/StripeInvoice.interface"
import Subscription, { Items, Plan, StripeCoupon } from "@/types/Subscription.interface"
import SubscriptionOption from "@/types/SubscriptionOption.interface"

export interface SubscriptionOptionResponse {
    active: boolean
    plans: SubscriptionOption[]
}

interface State {
    selectedCoupon: StripeCoupon
    subscription: Subscription
    subscriptionOptions: SubscriptionOption[]
}

const defaults = () => {
    return {
        selectedCoupon: null,
        subscription: null,
        subscriptionOptions: []
    }
}

export default {
    state: defaults(),
    mutations: {
        selectedCouponSet (state: State, coupon: StripeCoupon): void {
            state.selectedCoupon  = coupon
        },
        subscriptionSet (state: State, subscription: Subscription): void {
            state.subscription = subscription
        },
        subscriptionOptionsSet (state: State, options: SubscriptionOptionResponse): void {
            if(!options.active) {
                state.subscriptionOptions = options.plans
            }
        },
        subscriptionReset (state: State): void {
            Object.assign(state, defaults())
        }
    },
    actions: {
        subscriptionCancel ({ commit, getters }): Promise<Subscription> {
            //  ASsemble the API endpoint
            const url = `${getters.api}/subscription`

            //  Send the request
            return window.axios.delete(url).then(response => {
                //  Set our canceled subscription in the store
                commit('subscriptionSet', response.data)

                //  Return true for successful cancellation
                return true
            }).catch(() => null)
        },
        subscriptionFind ({ commit }, params): Promise<Subscription> {
            //  ASsemble the API endpoint
            const url = `/subscription?${window.toUrlParams(params)}`

            //  Send the request
            return window.axios.get(url).then(response => {
                //  Set our found subscription
                commit('subscriptionSet', response.data)

                //  Return true because we successfully found the sub
                return true
            }).catch(() => null)
        },
        subscriptionGet ({ commit, getters }): Promise<Subscription> {
            //  ASsemble the API endpoint
            const url = `${getters.api}/subscription`

            //  Send the request
            return window.axios.get(url).then(response => {
                //  Set our subscription we found
                commit('subscriptionSet', response.data)

                //  Return the found subscription
                return response.data
            }).catch(() => null)
        },
        subscriptionReactivate ({ commit, getters }): Promise<Subscription> {
            //  Assemble the API endpoint
            const url = `${getters.api}/subscription/reactivate`

            //  Send the request
            return window.axios.get(url).then(response => {
                //  Set our subscription
                commit('subscriptionSet', response.data)

                //  Return our subscription
                return response.data
            }).catch(() => null)
        },
        subscriptionUpcoming ({ getters }, payload: object): Promise<StripeInvoice> {
            //  ASsemble the API endpoint
            const url = `${getters.api}/subscription/upcoming`

            //  Send the request
            return window.axios.post(url, payload).then(response => {
                //  Return the found stripe invoice
                return response.data
            }).catch(() => null)
        },
        subscriptionUpdate ({ commit, getters }, plan: Plan): Promise<Subscription> {
            //  ASsemble the API endpoint
            const url = `${getters.api}/subscription`

            //  Send the request
            return window.axios.post(url, plan).then(response => {
                //  Set our updated subscription
                commit('subscriptionSet', response.data)

                //  Return the updaterd subscription
                return response.data
            }).catch(() => null)
        },
        subscriptionChangeUsers ({ commit, getters }, quantity: number): Promise<Subscription> {
            //  Create our payload
            const payload = {
                type: 'user',
                quantity: quantity
            }

            //  ASsemble the API endpoint
            const url = `${getters.api}/subscription/feature`

            //  Send the request
            return window.axios.post(url, payload).then(response => {
                //  Set our updated subscription
                commit('subscriptionSet', response.data)

                //  Return the updaterd subscription
                return response.data
            }).catch(() => null)
        },
        subscriptionOptionsGet ({ commit }, params: object): Promise<SubscriptionOption[]> {
            //  ASsemble the API endpoint
            const url = `/billing/plans?${window.toUrlParams(params)}`

            //  Send the request
            return window.axios.get(url).then(response => {
                //  Set our subscription options in the store
                commit('subscriptionOptionsSet', response.data)

                //  Return our found options
                return response.data
            }).catch(() => null)
        },
        couponsGet ({ commit }, discountCode: string): Promise<StripeCoupon> {
            //  ASsemble the API endpoint
            const url = `/billing/coupons/${discountCode}`

            //  Send the request
            return window.axios.get(url).then(response => {
                //  Set our selected coupon in the store
                commit('selectedCouponSet', response.data)

                //  Return our found coupon
                return response.data
            }).catch(() => null)
        },
        couponsPost ({ commit, getters }): Promise<Subscription> {
            //  Grab our selected coupon
            const coupon = getters.selectedCoupon

            //  Create our payload
            const payload = {
                code: coupon.id
            }

            //  Assemble the API endpoint
            const url = `${getters.api}/subscription/coupons`

            //  Send the request
            return window.axios.post(url, payload).then(response => {
                //  Set our subscription in the store
                commit('subscriptionSet', response.data)

                //  Return the subscription
                return response.data
            }).catch(() => null)
        },
        couponsRemove ({ commit, getters }): Promise<Subscription> {
            //  Assemble the API endpoint
            const url = `${getters.api}/subscription/coupons`

            //  Send the request
            return window.axios.delete(url).then(response => {
                //  Update our subscription
                commit('subscriptionSet', response.data)

                //  Return the subscription
                return response.data
            }).catch(() => null)
        }
    },
    getters: {
        getEnabled: (_, getters) => (type: string): boolean => {
            //  Get our stripe plan
            const plan = getters.plan

            //  Get our subscription items
            const items = getters.subscriptionItems

            //  If no plan, return false
            if (!plan) return false

            //  Else, define our prop
            const prop = `${type}_enabled`

            //  Grab our metadata field
            const meta = window.getProps(plan, ['metadata', prop], null)

            //  If meta is null, it doesn't exist so return true (default behavior)
            if (!meta) return true

            //  Get metadata for plan with prop (boolean value)
            const enabled = meta === 'true'

            //  If we have no items, just return enabled flag
            if (!items || !items.length) return enabled

            //  Else, grab our feature plan
            const featurePlan = items.find(x => 
                x.plan.nickname.includes(`MessageDesk ${type.toProperCase()}`)
            )

            //  Return true if either feature plan or normal plan is true
            return enabled || featurePlan != null
        },
        getLimit: (_, getters) => (type: string, useItems: boolean = true, stripePlan: Plan = null): number => {
            //  Get our stripe plan
            const plan = stripePlan || getters.plan

            //  Get our subscription items
            const items = useItems ? getters.subscriptionItems : null

            //  If no plan, return null
            if (!plan) return null

            //  Our prop
            const prop = `${type}_limit`

            //  Else, get the metadata for this plan with the specified prop
            const limit = parseInt(window.getProps(plan,['metadata',prop], '0'))

            //  If we have no items, return the limit
            if (!items || !items.length) return limit

            //  Grab the feature plan
            const featurePlans = items.filter((x) =>
                x.plan.nickname.includes(`MessageDesk ${type.toProperCase()}s`)
            )

            //  Our extra features starts at 0
            let extraFeatures = 0

            //  Iteraete through all of our feature plans
            for (let i = 0; i < featurePlans.length; i++) {
                //  Ref to our current feature plan
                let featurePlan = featurePlans[i]

                //  Get the quantity for this plan
                let quantity = parseInt(window.getProps(featurePlan, ['quantity'], '0'))

                //  Get the count for this plan
                let count = parseInt(window.getProps(featurePlan, ['plan', 'metadata', 'count'], '0'))

                //  Get the extra feature for this plan
                let extraFeature = quantity * count

                //  Add it to our extra features
                extraFeatures += extraFeature
            }

            //  Return limit + extra features
            return limit + extraFeatures
        },
        getPlan: (_, getters) => (tier: string, interval: string): Plan => {
            //  Based on our interval, get the appropriate plans
            const plans = interval === 'year' ? getters.yearOptions : getters.monthOptions

            //  Iterate through our plans
            for (let i = 0; i < plans.length; i++) {
                //  Grab the metadata and product tier
                let pTier = plans[i].metadata?.product_tier

                //  If no product tier then skip
                if (!pTier) continue

                //  Else, check if the tier matches our provided tier
                let match = pTier === tier

                //  If it does, return this plan
                if (match) return plans[i]
            }

            //  If we still can't find anything, return null :/
            return null
        },
        isUpgrade: (_, getters) => (plan: Plan): boolean => {
            //  If canceled, anything is an upgrade
            if (getters.tenant.account_status === 'canceled') return true

            //  If trialing, anything is an upgrade
            if (getters.tenant.account_status === 'trialing') return true

            //  If trial expired, anything is an upgrade
            if (getters.tenant.account_status === 'trial_expired') return true

            //  If no currrent plan, anything is an upgrade
            if (!getters.plan) return true

            //  Otherwise go by product tier (plan index)
            return plan.metadata.index > getters.plan.metadata.index
        },
        meteredPlan (state: State): Plan {
            //  Grab our subscription
            const sub = state.subscription

            //  If a subscription does NOT exist, return nothing
            if (!sub) return null

            //  If no items then return null
            if (!sub.items.data || !sub.items.data.length) return null

            //  Else, iterate through our items
            for (let i = 0; i < sub.items.data.length; i++) {
                //  If no plan, skip
                if (!sub.items.data[i].plan) continue

                //  Else, check if this is our metered plan
                if (sub.items.data[i].plan.metadata.metered == 'true') return sub.items.data[i].plan
            }

            //  If nothing was found, return null
            return null
        },
        meteredRate (state: State, getters): number {
            //  If no subscription, return 0.04
            if (!state.subscription) return 0.04

            //  Grab our plan
            const plan = getters.meteredPlan

            //  If no plan, return 0.04
            if (!plan) return 0.04

            //  Else, return the rate on this plan
            return 0.01 * plan.amount
        },
        monthOptions (state: State): SubscriptionOption[] {
            if (state.subscriptionOptions) {
                return state.subscriptionOptions.filter(x => {
                    return x.interval === 'month' && x.metadata.is_active === 'true' &&
                    x.metadata.default_start_plan !== 'true'
                })
            }
        },
        plan (_, getters): Plan {
            return getters.stripePlan
        },
        productTier (_, getters): string {
            //  Grab our plan
            const plan = getters.plan

            //  If the plan does NOT exist, return nothing
            if (!plan) return null

            //  If the plan and metadata exist, return the product tier
            if (plan?.metadata) {
                return plan.metadata.product_tier
            }

            //  Otherwise, return null
            return null
        },
        stripePlan (state: State): Plan {
            //  Grab our subscription
            const sub = state.subscription

            //  If a subscription does NOT exist, return nothing
            if (!sub) return null

            //  If this subscription has just 1 plan, then go ahead and return the plan
            if (sub.plan) return sub.plan

            //  If there isn't 1 plan, check if we have items, and if there isn't any, we have a problem
            if (!sub.items || !sub.items.data || !sub.items.data.length) return null

            //  If we have items, we need to find the actual subscription plan one
            if (sub.items.data && sub.items.data.length) {
                //  Iterate through every single subscription item attached
                for (let i = 0; i < sub.items.data.length; i++) {
                    //  If this subscription item doesn't have a plan, skip it
                    if (!sub.items.data[i].plan) continue;

                    //  If this subscription item does have a plan, check the metadata on it for property "flat" and return it
                    if (sub.items.data[i].plan.metadata.flat == 'true') return sub.items.data[i].plan
                }
            }

            //  If we still can't find anything, return a blank object
            return null
        },
        subscriptionItems (state: State): Items {
            return window.getProps(state.subscription, ['items', 'data'], null)
        },
        selectedCoupon (state: State): StripeCoupon {
            return state.selectedCoupon
        },
        subscription (state: State): Subscription {
            return state.subscription
        },
        subscriptionOptions (state: State): SubscriptionOption[] {
            return state.subscriptionOptions
        },
        lapsedSubscriptionStates (): string[] {
            return ['past_due', 'unpaid', 'canceled'];
        },
        trialing (state: State, getters): boolean {
            //  If we have no subscription or state, do nothing
            if (!getters.plan || !getters.subscription) return false

            //  Grab our plan
            const plan = getters.plan || {}

            //  Check if the tenant has a default start plan
            const hasDefaultPlan = plan.metadata?.default_start_plan === 'true'

            //  Check fi the tenant is currently in trial period
            const isTrialing = getters.subscription.status === 'trialing'

            //  The tenant is trialing if they have a default plan OR are in trial period
            return hasDefaultPlan || isTrialing
        },
        yearOptions (state: State): SubscriptionOption[] {
            if (state.subscriptionOptions) {
                return state.subscriptionOptions.filter(x => {
                    return x.interval === 'year' && x.metadata.is_active === 'true' &&
                    x.metadata.default_start_plan !== 'true'
                })
            }
        },
    }
}
